import PropTypes from 'prop-types';
import { clone, compose, filter, find, path, prop, propEq, reverse, sortBy } from 'ramda';
import { connect } from 'react-redux';
import { readEndpoint } from 'redux-json-api';
import { createSelector } from 'reselect';
import React, { Component } from 'react';

import { withStyles } from '@material-ui/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';

import { inboxOpen, inboxClose } from '../../modules/actions/layout';

import InvitedDriverMessage from './InvitedDriverMessage';
import NoCapacityMessage from './NoCapacityMessage';
import ReservedVehicleMessage from './ReservedVehicleMessage';
import RideMessage from './RideMessage';
import RouteBlockage from './RouteBlockage';


class Inbox extends Component {
  static propTypes = {
    readEndpoint: PropTypes.func.isRequired,

    open: PropTypes.bool.isRequired,
    inboxOpen: PropTypes.func.isRequired,
    inboxClose: PropTypes.func.isRequired,
  };

  renderMessage(message) {
    const { routeBlockageNames } = this.props;
    const type = path(['attributes', 'sender-type-name'], message) || 'default';

    switch (type) {
      case 'invited-driver': {
        return (
          <InvitedDriverMessage
            key={'message-' + message.id}
            message={message}
            routeBlockageNames={routeBlockageNames}
          />
        );
      }
      case 'reserved-vehicle': {
        return (
          <ReservedVehicleMessage
            key={'message-' + message.id}
            message={message}
            routeBlockageNames={routeBlockageNames}
          />);
      }
      case 'ride': {
        return (
          <RideMessage
            key={'message-' + message.id}
            message={message}
            routeBlockageNames={routeBlockageNames}
          />);
      }
      case 'route-blockage': {
        return (
          <NoCapacityMessage
            key={`message-${message.id}`}
            message={message}
            routeBlockageNames={routeBlockageNames}
          />
        );
      }
    }
  }

  render() {
    const {
      classes, dashboardMessages, open, routeBlockages, routeBlockageNames,
    } = this.props;

    return (
      <Drawer
        classes={{ root: classes.drawer }}
        anchor="right"
        open={open}
        ModalProps={{ hideBackdrop: true }}
        PaperProps={{ classes: { root: classes.drawerPaper } }}
      >
        <List>
          <ListSubheader>Stremmingen</ListSubheader>
          {routeBlockages.map(rb => (
            <RouteBlockage
              key={'route-blockage-' + rb.id}
              routeBlockage={rb}
              routeBlockageNames={routeBlockageNames}
            />
          ))}
        </List>
        <List>
          <ListSubheader>Berichten</ListSubheader>
          {dashboardMessages.map(message => this.renderMessage(message))}
        </List>
      </Drawer>
    );
  };
}

/**
 * Returns a list of dashboard messages ordered by numerical id value, and
 * merged where appropriate.
 */
function selectDashboardMessages(state) {
  const messages = path(['api', 'dashboard-message', 'data'], state) || [];
  const makeInt = v => parseInt(v, 10);
  let ms_sorted = sortBy(compose(makeInt, prop('id')), messages);
  let ms_final = [];

  /**
   * Merge matching subsequent dashboard messages, and reverse the order of our
   * list. Use clones to avoid overwriting Redux state.
   */
  while (ms_sorted.length > 0) {
    const item = clone(ms_sorted.pop());

    if (ms_final.length === 0) {
      ms_final.push(item);
    } else {
      let prev = ms_final[ms_final.length - 1];
      let pp = path(['attributes', 'payload'], prev);
      let ip = path(['attributes', 'payload'], item);

      if (pp && ip &&
          (pp['company'] === ip['company']) &&
          (pp['route-blockage'] === ip['route-blockage']) &&
          (prev.attributes['sender-type-name'] === item.attributes['sender-type-name']) &&
          (prev.attributes.action === item.attributes.action) &&
          (prev.attributes.action === 'CREATE')) {
        if (!(prev.attributes['sender-ids'])) {
          prev.attributes['sender-ids'] = [prev.attributes['sender-id']];
          prev.attributes['merged'] = true;
        }

        prev.attributes['sender-ids'].push(item.attributes['sender-id']);
      } else {
        ms_final.push(item);
      }
    }
  }

  return ms_final;
}

function selectRouteBlockages(state) {
  const rbs = path(['api', 'route-blockage', 'data'], state) || [];
  const makeInt = v => parseInt(v, 10);

  const isActive = rb => {
    const endedAt = path(['attributes', 'ended-at'], rb);

    return !endedAt;
  };

  const arbs = filter(isActive)(rbs);

  return reverse(sortBy(compose(makeInt, prop('id')), arbs));
}

const _selectRouteBlockages = state => path(['api', 'route-blockage', 'data'], state) || [];
const _selectStops = state => path(['api', 'stop', 'data'], state) || [];
const getRouteBlockageNames = createSelector([_selectRouteBlockages, _selectStops], (rbs, ss) => {
  let namesMap = {};

  rbs.forEach(rb => {
    const sfid = path(['relationships', 'stop-from', 'data', 'id'], rb);
    const stid = path(['relationships', 'stop-to', 'data', 'id'], rb);
    const sf = find(propEq('id', sfid))(ss);
    const st = find(propEq('id', stid))(ss);

    namesMap[String(rb.id)] = (path(['attributes', 'name'], sf) || 'Onbekend') + ' naar ' +
      (path(['attributes', 'name'], st) || 'Onbekend');
  });

  return namesMap;
});

const mapStateToProps = (state) => ({
  dashboardMessages: selectDashboardMessages(state),
  routeBlockages: selectRouteBlockages(state),
  routeBlockageNames: getRouteBlockageNames(state),
  stops: path(['api', 'stop', 'data'], state) || [],
  open: path(['layout', 'inboxOpen'], state) || false,
});

const mapDispatchToProps = {
  readEndpoint,
  inboxOpen,
  inboxClose,
};

const styles = {
  drawer: {
    left: 'auto',
    right: 0,
  },
  drawerPaper: {
    width: 360,
  },
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Inbox));
